/**
 * The review list for the `proposal:value` output kind on the generic agent
 * report page: every value this agent proposed that waits for a person, with
 * accept and reject (a reason is required — it teaches the next estimate) and
 * bulk accept above a calibrated confidence when an administrator turned it
 * on. Accept and reject go through the same store functions the item's Values
 * section uses, so the triggers that stamp outcomes fire the same way.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Callout } from '../../ds';
import { useValueProposalStore } from '../../../stores/prioritization/valueProposalStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { calibrateConfidence, loadAgentCalibration, type CalibrationRow } from '../../../lib/agentCalibrationService';
import { when, type OutputReviewProps } from './types';
import { useOpenValueProposals } from './useOpenValueProposals';

export function ValueProposalsReview({ def, config, workspaceId, mayReview, onChanged, titleOf }: OutputReviewProps) {
  const userId = useUserStore((s) => s.userId);
  const currentRole = useUserStore((s) => s.currentRole) || 'workspace-member';
  const acceptProposal = useValueProposalStore((s) => s.acceptProposal);
  const rejectProposal = useValueProposalStore((s) => s.rejectProposal);
  const open = useOpenValueProposals(def.agentType);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [calibration, setCalibration] = useState<CalibrationRow[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => { void loadAgentCalibration(workspaceId, def.agentType).then(setCalibration); }, [workspaceId, def.agentType]);

  const acceptorId = useCallback(async () => {
    const supabase = getSafeSupabaseClient();
    if (supabase) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) return session.user.id;
    }
    return userId;
  }, [userId]);

  // Bulk accept (owner ruling 2026-10-13): administrator-enabled, calibrated buckets only, above the floor.
  const floor = config?.bulkAcceptFloor ?? 0.8;
  const eligible = useMemo(
    () => (def.bulkAcceptAllowed && config?.bulkAcceptEnabled
      ? open.filter((p) => { const c = calibrateConfidence(p.confidence ?? 0, calibration, p.attributeKey); return c.calibrated && c.confidence >= floor; })
      : []),
    [def.bulkAcceptAllowed, config?.bulkAcceptEnabled, open, calibration, floor],
  );

  const run = async (id: string, act: () => Promise<unknown>) => {
    setBusyId(id);
    setError(null);
    try { await act(); onChanged(); } catch (e) { setError(e instanceof Error ? e.message : 'Not saved'); } finally { setBusyId(null); }
  };

  const bulkAccept = async () => {
    setBulkBusy(true);
    setError(null);
    try {
      const by = await acceptorId();
      // One acceptance per proposal, each its own row and each reversible alone.
      for (const p of eligible) {
        await acceptProposal({ proposalId: p.id, acceptedBy: by, acceptorRole: currentRole, acceptanceMethod: 'facilitator_override', acceptanceNote: 'Bulk accept above calibrated confidence' });
      }
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk accept failed');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      {error && <Callout tone="coral">{error}</Callout>}
      {!mayReview && open.length > 0 && (
        <Callout tone="neutral">Reviewing what {def.name} proposes needs one of its review roles. A workspace administrator can grant one.</Callout>
      )}
      {mayReview && def.bulkAcceptAllowed && config?.bulkAcceptEnabled && (
        <div className="card flex flex-wrap items-center justify-between gap-3 bg-[var(--slate-dim)] border-[var(--slate)]">
          <p className="text-xs text-[var(--text-sec)]">
            {eligible.length === 0
              ? `Nothing to accept together yet: a proposal qualifies once its confidence bucket is calibrated and clears ${Math.round(floor * 100)}%.`
              : `${eligible.length} proposal${eligible.length === 1 ? '' : 's'} sit in a calibrated bucket above ${Math.round(floor * 100)}%. Each is logged as its own acceptance and can be reversed alone.`}
          </p>
          {eligible.length > 0 && (
            <Button variant="primary" size="sm" onClick={() => void bulkAccept()} disabled={bulkBusy}>
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Accept {eligible.length} together
            </Button>
          )}
        </div>
      )}
      {open.length === 0 ? (
        <div className="card text-center py-10">
          <Sparkles className="h-6 w-6 mx-auto text-[var(--text-ter)]" />
          <p className="text-sm font-medium text-[var(--text-pri)] mt-2">Nothing waiting</p>
          <p className="text-xs text-[var(--text-ter)] mt-1">Run {def.name}, or ask for an estimate from an item's Values section.</p>
        </div>
      ) : open.map((p) => (
        <div key={p.id} className="card space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/prioritization/items/${p.itemId}?tab=values`} className="text-sm font-medium text-[var(--text-pri)] underline">{titleOf(p.itemId)}</Link>
            <span className="text-xs text-[var(--text-ter)]">{p.attributeKey}</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-pri)] border border-[var(--slate)]">
              <Sparkles className="h-3 w-3" /> {String(p.proposedValue)}
            </span>
            {p.confidence != null && <span className="text-xs text-[var(--text-ter)]">{Math.round(p.confidence * 100)}% confidence</span>}
            <span className="ml-auto text-xs text-[var(--text-ter)]">{when(p.proposedAt instanceof Date ? p.proposedAt.toISOString() : String(p.proposedAt))}</span>
          </div>
          {p.rationale && <p className="text-xs text-[var(--text-ter)] italic">"{p.rationale}"</p>}
          {mayReview && (rejectingId === p.id ? (
            <div className="space-y-2 border-t border-[var(--rule)] pt-2">
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} aria-label="Why it is wrong"
                placeholder="Why is it wrong? (required — it teaches the next estimate)"
                className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] focus:outline-none focus:ring-1 focus:ring-[var(--mustard)] resize-none" />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setRejectingId(null); setReason(''); }}>Cancel</Button>
                {reason.trim() && (
                  <Button variant="danger" size="sm" disabled={busyId === p.id}
                    onClick={() => void run(p.id, async () => { await rejectProposal({ proposalId: p.id, rejectedBy: userId, reason: reason.trim() }); setRejectingId(null); setReason(''); })}>
                    {busyId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Reject
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setRejectingId(p.id); setReason(''); }} disabled={busyId === p.id}><X className="h-3.5 w-3.5" /> Reject</Button>
              <Button variant="primary" size="sm" disabled={busyId === p.id}
                onClick={() => void run(p.id, async () => acceptProposal({ proposalId: p.id, acceptedBy: await acceptorId(), acceptorRole: currentRole, acceptanceMethod: 'facilitator_override' }))}>
                {busyId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Accept
              </Button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
